import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BackgroundIcons from '../components/BackgroundIcons';
import NotificationModal from '../components/NotificationModal';
import RitualDetailsModal from '../components/RitualDetailsModal';
import { apiService } from '../services/apiService';

const ritualIcons = [
  { emoji: '🎭', size: '6xl', opacity: '15' },
  { emoji: '📖', size: '4xl', opacity: '15' },
  { emoji: '🕯️', size: '5xl', opacity: '15' },
  { emoji: '✨', size: '3xl', opacity: '15' },
  { emoji: '🌍', size: '7xl', opacity: '15' },
  { emoji: '💫', size: '4xl', opacity: '15' }
];

const RitualsDashboard = () => {
  const navigate = useNavigate();
  const [rituals, setRituals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCulture, setSelectedCulture] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });
  const [selectedRitual, setSelectedRitual] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, ritual: null });

  useEffect(() => {
    loadRituals();
    setIsVisible(true);
  }, []);

  const loadRituals = async () => {
    try {
      const response = await apiService.userRituals.getAll();
      setRituals(response.rituals || []);
    } catch (error) {
      console.error('Error loading rituals:', error);
      setNotification({
        show: true,
        title: 'Error',
        message: 'Failed to load your saved rituals. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const stats = {
    total: rituals.length,
    favorites: rituals.filter(r => r.is_favorite).length,
    cultures: [...new Set(rituals.map(r => r.culture))].length,
    recent: rituals.filter(r => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(r.created_at) > weekAgo;
    }).length
  };

  // Filter rituals
  const filteredRituals = rituals.filter(ritual => {
    // Filter by favorites
    if (filter === 'favorites' && !ritual.is_favorite) return false;
    
    // Filter by culture
    if (selectedCulture && ritual.culture !== selectedCulture) return false;
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ritual.name.toLowerCase().includes(query) ||
        ritual.culture_display_name.toLowerCase().includes(query) ||
        ritual.description.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  // Get unique cultures
  const cultures = [...new Set(rituals.map(r => r.culture))];

  const getEmojiForCulture = (culture) => {
    const emojiMap = {
      'western': '💍',
      'indian': '🔥',
      'chinese': '🍵',
      'jewish': '✡️',
      'african': '🥁',
      'islamic': '☪️',
      'japanese': '🍶',
      'korean': '🎎',
      'latin_american': '💃',
      'celtic': '☘️',
      'southeast_asian': '🌺',
      'modern': '✨'
    };
    return emojiMap[culture] || '🎊';
  };

  const handleToggleFavorite = async (ritualId) => {
    try {
      await apiService.userRituals.toggleFavorite(ritualId);
      setRituals(rituals.map(r => 
        r.id === ritualId ? { ...r, is_favorite: !r.is_favorite } : r
      ));
      if (selectedRitual?.id === ritualId) {
        setSelectedRitual({ ...selectedRitual, is_favorite: !selectedRitual.is_favorite });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setNotification({
        show: true,
        title: 'Error',
        message: 'Failed to update favorite status',
        type: 'error'
      });
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.userRituals.delete(deleteConfirm.ritual.id);
      setRituals(rituals.filter(r => r.id !== deleteConfirm.ritual.id));
      setDeleteConfirm({ show: false, ritual: null });
      setSelectedRitual(null);
      setNotification({
        show: true,
        title: 'Ritual Removed',
        message: '🗑️ Ritual has been removed from your collection',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting ritual:', error);
      setNotification({
        show: true,
        title: 'Delete Failed',
        message: 'Failed to remove ritual. Please try again.',
        type: 'error'
      });
    }
  };

  const handleUpdateNotes = async (notes) => {
    try {
      await apiService.userRituals.updateNotes(selectedRitual.id, notes);
      setRituals(rituals.map(r => 
        r.id === selectedRitual.id ? { ...r, notes } : r
      ));
      setSelectedRitual({ ...selectedRitual, notes });
      setNotification({
        show: true,
        title: 'Notes Updated',
        message: '📝 Your notes have been saved',
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating notes:', error);
      setNotification({
        show: true,
        title: 'Update Failed',
        message: 'Failed to save notes. Please try again.',
        type: 'error'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={ritualIcons} />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        
        {/* Beautiful Header - VowAI Style */}
        <div className="text-center mb-16 relative">
          
          {/* Back to Dashboard Button */}
          <div className="absolute top-0 left-0">
            <Link 
              to="/dashboard"
              className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-6 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
            >
              <span className="mr-2 text-xl group-hover:animate-bounce">←</span>
              Back to Dashboard
              <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
            </Link>
          </div>
          
          <div className={`inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-purple-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce transition-all duration-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}>
            <span className="text-4xl animate-pulse">🎭</span>
          </div>
          
          <h1 className={`text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            My Cultural Rituals
          </h1>
          
          <div className="flex items-center justify-center mb-6">
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            <div className="mx-4 text-3xl animate-bounce">📖</div>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
          </div>
          
          <p className={`text-2xl text-gray-700 font-light italic transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Your collection of meaningful wedding traditions ✨
          </p>
          
          <div className="mt-8">
            <span className="px-6 py-3 bg-gradient-to-r from-purple-400 to-pink-500 text-white rounded-full font-bold text-lg shadow-xl animate-pulse">
              {stats.total} Ritual{stats.total !== 1 ? 's' : ''} Saved
            </span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="group bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-rose-100 hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Rituals</p>
                <p className="text-3xl font-bold text-rose-600">{stats.total}</p>
              </div>
              <div className="bg-rose-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">🎭</span>
              </div>
            </div>
          </div>

          <div className="group bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-gold-100 hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Favorites</p>
                <p className="text-3xl font-bold text-gold-600">{stats.favorites}</p>
              </div>
              <div className="bg-gold-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">⭐</span>
              </div>
            </div>
          </div>

          <div className="group bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-purple-100 hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Cultures</p>
                <p className="text-3xl font-bold text-purple-600">{stats.cultures}</p>
              </div>
              <div className="bg-purple-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">🌍</span>
              </div>
            </div>
          </div>

          <div className="group bg-white/70 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-pink-100 hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Recent (7 days)</p>
                <p className="text-3xl font-bold text-pink-600">{stats.recent}</p>
              </div>
              <div className="bg-pink-100 rounded-full p-3">
                <span className="text-2xl group-hover:animate-bounce">🆕</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters & Search */}
        {!loading && rituals.length > 0 && (
          <div className={`mb-8 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-purple-200/50 p-6">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                  <span className="text-2xl">🔍</span>
                </div>
                <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Filter & Search
                </h3>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-4">
                {/* Search Bar */}
                <div className="md:col-span-2 relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-400">
                    <span className="text-xl">🔍</span>
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search rituals by name, culture, or description..."
                    className="w-full pl-12 pr-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-400 transition-all duration-300 bg-white/80 font-medium"
                  />
                </div>

                {/* Culture Filter */}
                <div className="relative">
                  <select
                    value={selectedCulture}
                    onChange={(e) => setSelectedCulture(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-400 transition-all duration-300 bg-white/80 font-medium appearance-none"
                  >
                    <option value="">All Cultures</option>
                    {cultures.map((culture) => (
                      <option key={culture} value={culture}>
                        {getEmojiForCulture(culture)} {culture.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-purple-400 pointer-events-none">
                    <span className="text-xl">🌍</span>
                  </div>
                </div>
              </div>

              {/* Filter Buttons */}
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-6 py-2 rounded-xl font-bold text-sm transition-all duration-300 ${
                    filter === 'all'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg scale-105'
                      : 'bg-white border-2 border-purple-200 text-purple-600 hover:border-purple-400'
                  }`}
                >
                  <span className="mr-1">🎭</span>
                  All Rituals
                </button>
                <button
                  onClick={() => setFilter('favorites')}
                  className={`px-6 py-2 rounded-xl font-bold text-sm transition-all duration-300 ${
                    filter === 'favorites'
                      ? 'bg-gradient-to-r from-gold-500 to-yellow-500 text-white shadow-lg scale-105'
                      : 'bg-white border-2 border-gold-200 text-gold-600 hover:border-gold-400'
                  }`}
                >
                  <span className="mr-1">⭐</span>
                  Favorites
                </button>
                {(searchQuery || selectedCulture || filter !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCulture('');
                      setFilter('all');
                    }}
                    className="px-6 py-2 bg-gray-100 border-2 border-gray-300 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 transition-all duration-300"
                  >
                    <span className="mr-1">✖️</span>
                    Clear All
                  </button>
                )}
              </div>

              {/* Results Count */}
              <div className="mt-4 text-center">
                <span className="text-sm text-gray-600 font-medium">
                  Showing {filteredRituals.length} of {rituals.length} ritual{rituals.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-rose-500 mx-auto mb-4"></div>
            <p className="text-xl text-gray-600">Loading your rituals...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && rituals.length === 0 && (
          <div className="text-center py-20">
            <div className="text-8xl mb-6 animate-bounce">🎭</div>
            <h3 className="text-3xl font-serif font-bold text-gray-700 mb-4">
              No Rituals Saved Yet
            </h3>
            <p className="text-xl text-gray-600 mb-8">
              Start exploring beautiful cultural traditions for your wedding
            </p>
            <Link to="/wedcraft-studio/cultural-ritual-explainer">
              <button className="group relative overflow-hidden bg-gradient-to-r from-purple-500 via-pink-500 to-gold-500 text-white py-4 px-12 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
                <span className="relative z-10 flex items-center">
                  <span className="mr-2 text-2xl group-hover:animate-bounce">✨</span>
                  Explore Cultural Rituals
                </span>
              </button>
            </Link>
          </div>
        )}

        {/* Ritual Cards Grid */}
        {!loading && filteredRituals.length > 0 && (
          <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredRituals.map((ritual) => (
                <div
                  key={ritual.id}
                  onClick={() => setSelectedRitual(ritual)}
                  className="group relative bg-gradient-to-br from-white via-purple-50/30 to-pink-50/30 backdrop-blur-lg rounded-3xl shadow-xl border-2 border-transparent hover:border-purple-300 overflow-hidden hover:shadow-2xl transform hover:scale-105 transition-all duration-500 cursor-pointer"
                >
                  {/* Animated Gradient Border Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-400 to-gold-400 opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-3xl"></div>
                  
                  {/* Floating Icons */}
                  <div className="absolute top-4 right-4 text-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 animate-pulse">🎭</div>
                  <div className="absolute bottom-4 left-4 text-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 animate-pulse delay-100">✨</div>
                  
                  {/* Culture Badge */}
                  <div className="absolute top-4 left-4 z-10">
                    <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full shadow-lg">
                      <span className="mr-1">{getEmojiForCulture(ritual.culture)}</span>
                      {ritual.culture_display_name}
                    </span>
                  </div>

                  {/* Favorite Badge */}
                  {ritual.is_favorite && (
                    <div className="absolute top-4 right-4 z-10 text-3xl animate-bounce drop-shadow-lg">⭐</div>
                  )}

                  {/* Card Content */}
                  <div className="p-6 pt-16 relative z-10">
                    {/* Shimmer Effect on Hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    
                    {/* Ritual Name */}
                    <h3 className="text-2xl font-serif font-bold mb-3 group-hover:scale-105 transition-transform duration-300 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      {ritual.name}
                    </h3>
                    
                    {/* Description Preview */}
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {ritual.description}
                    </p>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {ritual.duration_minutes && (
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                          ⏱️ {ritual.duration_minutes} min
                        </span>
                      )}
                      {ritual.complexity && (
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full capitalize">
                          📊 {ritual.complexity}
                        </span>
                      )}
                    </div>

                    {/* Notes Preview */}
                    {ritual.notes && (
                      <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                        <p className="text-xs text-gray-700 italic line-clamp-2">
                          📝 {ritual.notes}
                        </p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRitual(ritual);
                        }}
                        className="group/viewbtn flex-1 relative overflow-hidden bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-xl font-bold text-sm hover:shadow-lg transform hover:scale-105 transition-all duration-300 animate-pulse"
                      >
                        <span className="relative z-10 flex items-center justify-center">
                          <span className="mr-1 group-hover/viewbtn:animate-bounce">✨</span>
                          View Details
                        </span>
                        {/* VowAI signature glow effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-400/20 to-purple-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(ritual.id);
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-gold-200 to-yellow-200 hover:from-gold-300 hover:to-yellow-300 rounded-xl transition-all duration-300 text-xl border-2 border-gold-300 transform hover:scale-110 hover:rotate-12 shadow-md hover:shadow-lg"
                      >
                        {ritual.is_favorite ? '⭐' : '☆'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm({ show: true, ritual });
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-red-200 to-rose-200 hover:from-red-300 hover:to-rose-300 text-red-700 rounded-xl transition-all duration-300 text-xl border-2 border-red-300 transform hover:scale-110 hover:rotate-12 shadow-md hover:shadow-lg font-bold"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  {/* Magical Glow Effect on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-400/0 via-pink-400/0 to-gold-400/0 group-hover:from-purple-400/10 group-hover:via-pink-400/10 group-hover:to-gold-400/10 transition-all duration-500 rounded-3xl pointer-events-none"></div>
                </div>
              ))}
            </div>
          </div>
        )}
        
      </div>

      {/* Ritual Details Modal */}
      <RitualDetailsModal
        isOpen={!!selectedRitual}
        onClose={() => setSelectedRitual(null)}
        ritual={selectedRitual}
        onToggleFavorite={() => handleToggleFavorite(selectedRitual.id)}
        onDelete={() => {
          setSelectedRitual(null);
          setDeleteConfirm({ show: true, ritual: selectedRitual });
        }}
        onUpdateNotes={handleUpdateNotes}
      />

      {/* Delete Confirmation Modal */}
      <NotificationModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, ritual: null })}
        type="warning"
        title="Remove Ritual?"
        message="Are you sure you want to remove this ritual from your collection? This action cannot be undone."
        isConfirmation={true}
        onConfirm={handleDelete}
        confirmText="Yes, Remove"
        cancelText="Cancel"
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        title={notification.title}
        message={notification.message}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
      />
    </div>
  );
};

export default RitualsDashboard;
